import React, { useEffect, useState } from 'react';
import { Shell } from '../shell/Shell';
import { Badge, PageHeader, fmtDateTime, I, PencilIcon } from '../atoms';
import { getUserEmail } from '../auth';
import { CONFIG } from '../config';
import { useToasts, Toasts } from '../Toasts';
import { computeChangedFields, isStaleWriteConflict } from '../optimisticConcurrency';
import { usePlanningPermissions } from '../usePlanningPermissions';
import { errMessage, fmtCost, fmtTokens, renderMarkdown, runEvent, STALE_MESSAGE, useSettledReload } from '../pageUtils';
import {
  listPolicyInputs, getPolicyInput, emitCreatePolicyInput, emitUpdatePolicyInput, emitDeletePolicyInput,
  getPolicyInputUploadUrl, uploadPolicyInputDocumentToS3, emitConfirmPolicyInputDocument,
  emitDeletePolicyInputDocument, getPolicyInputDownloadUrl, generatePolicyInputOutput,
  getPolicyInputOutputUploadUrl, uploadPolicyInputOutputToS3,
  type PolicyInput, type OutputStatus, type PolicyScope,
} from '../api/policyInputs';

type AddToast = (type: 'success' | 'error' | 'info' | 'warn', message: string, persistent?: boolean) => string;
type DetailTab = 'details' | 'documents' | 'output';

function statusKind(status: OutputStatus): string {
  if (status === 'Generated') return 'pos';
  if (status === 'Stale') return 'warn';
  if (status === 'Failed') return 'neg';
  if (status === 'Generating') return 'info';
  return 'neutral';
}

function attributionText(item: PolicyInput): string | null {
  if (!item.output) return null;
  if (item.lastEditedBy) return `Edited by ${item.lastEditedBy} — originally authored by ${item.lastGenerationModel || 'AI'}`;
  return `Authored by ${item.lastGenerationModel || 'AI'}`;
}

function scopeLabel(item: Pick<PolicyInput, 'scope'>): string {
  return item.scope === 'Global' ? 'Shared library' : 'Elevations';
}

// List <-> detail navigation goes through window.location.hash so the browser Back button works.
export function PolicyInputsPage({ id }: { id?: string }) {
  const { can, loading: permLoading } = usePlanningPermissions();
  const canWrite = can('policy-inputs:write');
  const canManageGlobal = can('policy-inputs:manage-global');

  if (!permLoading && !can('policy-inputs:read')) {
    return (
      <Shell current="policy-inputs" crumbs={[{ label: 'Planning' }, { label: 'Policy Inputs' }]}>
        <div className="card">
          <div className="card-body" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
            You don't have permission to view this page.
          </div>
        </div>
      </Shell>
    );
  }

  if (id) return <PolicyInputDetail id={id} canWrite={canWrite} canManageGlobal={canManageGlobal} />;
  return (
    <PolicyInputList
      canWrite={canWrite}
      canManageGlobal={canManageGlobal}
      onOpen={openId => { window.location.hash = `#/policy-inputs/${openId}`; }}
    />
  );
}

function PolicyInputDrawer({ policyInput, canManageGlobal, onClose, onSaved, onCreated, onDeleted, onUpdateConflict, addToast }: {
  policyInput: PolicyInput | null;
  canManageGlobal: boolean;
  onClose: () => void;
  onSaved: (updated: PolicyInput) => void;
  onCreated: (id: string) => void;
  onDeleted: () => void;
  onUpdateConflict: () => void;
  addToast: AddToast;
}) {
  const [name, setName] = useState(policyInput?.name ?? '');
  const [jurisdiction, setJurisdiction] = useState(policyInput?.jurisdiction ?? '');
  const [scope, setScope] = useState<PolicyScope>(policyInput?.scope ?? (canManageGlobal ? 'Global' : 'Tenant'));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function save() {
    const trimmedName = name.trim();
    if (!trimmedName) { setSaveErr('Name is required'); return; }
    const trimmedJurisdiction = jurisdiction.trim();
    setSaving(true); setSaveErr(null);
    try {
      if (!policyInput) {
        // The server assigns its own id, so the new record is found by re-listing afterwards.
        await runEvent(connId => emitCreatePolicyInput({
          name: trimmedName,
          scope,
          ...(scope === 'Tenant' ? { tenantId: CONFIG.tenantId } : {}),
          jurisdiction: trimmedJurisdiction || undefined,
          performedBy: getUserEmail() ?? undefined,
        }, connId));
        const list = await listPolicyInputs();
        const created = list
          .filter(p => p.name === trimmedName && p.scope === scope)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
        if (created) { addToast('success', 'Created'); onCreated(created.id); }
        else { addToast('error', 'Created, but could not find the new record — refresh the list'); setSaving(false); }
        return;
      }

      const payload: Record<string, unknown> = { name: trimmedName, jurisdiction: trimmedJurisdiction };
      const changed = computeChangedFields(policyInput as unknown as Record<string, unknown>, payload);
      const expectedValues: Record<string, unknown> = {};
      for (const key of Object.keys(changed)) expectedValues[key] = (policyInput as unknown as Record<string, unknown>)[key];
      await runEvent(connId => emitUpdatePolicyInput(
        policyInput.id, { ...payload, performedBy: getUserEmail() ?? undefined }, connId, expectedValues,
      ));
      const updated = await getPolicyInput(policyInput.id);
      if (updated) { addToast('success', 'Saved'); onSaved(updated); }
      setSaving(false);
    } catch (e) {
      if (policyInput && e instanceof Error && isStaleWriteConflict(e.message)) onUpdateConflict();
      else setSaveErr(errMessage(e, 'Failed to save'));
      setSaving(false);
    }
  }

  async function remove() {
    if (!policyInput) return;
    if (!window.confirm(`Delete "${policyInput.name}"? This removes its uploaded documents and cannot be undone.`)) return;
    setDeleting(true); setSaveErr(null);
    try {
      await runEvent(connId => emitDeletePolicyInput(policyInput.id, getUserEmail() ?? undefined, connId));
      addToast('success', 'Policy input deleted');
      onDeleted();
    } catch (e) {
      setSaveErr(errMessage(e, 'Failed to delete'));
      setDeleting(false);
    }
  }

  const busy = saving || deleting;

  return (
    <>
      <div className="drawer-overlay" />
      <div className="drawer">
        <div className="drawer-hd">
          <span className="drawer-hd-title">{policyInput ? 'Edit policy input' : 'New policy input'}</span>
          <button className="btn btn-icon" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 1l12 12M13 1L1 13" /></svg>
          </button>
        </div>
        <div className="drawer-body" style={{ flex: 'none' }}>
          <div className="drawer-section">
            <div className="drawer-section-hd">Details</div>
            <div className="field">
              <label>Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. NPPF August 2026" />
            </div>
            <div className="field">
              <label>Jurisdiction</label>
              <input type="text" value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} placeholder="e.g. NPPF, South Gloucestershire" />
            </div>
            {!policyInput && (
              <div className="field">
                <label>Visibility</label>
                <select value={scope} onChange={e => setScope(e.target.value as PolicyScope)}>
                  {canManageGlobal && <option value="Global">Shared library — every organisation</option>}
                  <option value="Tenant">Elevations — only the Elevations app</option>
                </select>
              </div>
            )}
          </div>
        </div>
        {saveErr && <div style={{ padding: '0 20px 12px', color: 'var(--neg)', fontSize: 13 }}>{saveErr}</div>}
        <div className="drawer-ft">
          {policyInput && (
            <button className="btn btn-danger" onClick={() => void remove()} disabled={busy} style={{ marginRight: 'auto' }}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}

function PolicyInputList({ canWrite, canManageGlobal, onOpen }: {
  canWrite: boolean; canManageGlobal: boolean; onOpen: (id: string) => void;
}) {
  const { toasts, addToast, removeToast } = useToasts();
  const [items, setItems] = useState<PolicyInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    listPolicyInputs()
      .then(list => { if (!cancelled) setItems([...list].sort((a, b) => a.name.localeCompare(b.name))); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Shell current="policy-inputs" crumbs={[{ label: 'Planning' }, { label: 'Policy Inputs' }]}>
      <PageHeader
        title="Policy Inputs"
        subtitle="Named policy document sets whose summaries feed every planning assessment — e.g. the NPPF, or a local planning authority's local plan. Summarised using the policy analysis prompt in Settings."
        actions={(canWrite || canManageGlobal) && (
          <button className="btn btn-primary" onClick={() => setDrawerOpen(true)}>New policy input</button>
        )}
      />

      <div className="card">
        <div className="card-body flush">
          {error ? <div className="state-fill error">Failed to load policy inputs: {error}</div> : (
            <table className="tbl tbl-list">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th className="hide-mobile">Jurisdiction</th>
                  <th className="hide-mobile">Visibility</th>
                  <th className="hide-mobile">Documents</th>
                  <th className="hide-mobile">Updated</th>
                </tr>
              </thead>
              <tbody>
                {loading ? Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} style={{ '--skel-delay': `${i * 60}ms` } as React.CSSProperties}>
                    <td><span className="skel" style={{ width: 160 }} /></td>
                    <td><span className="skel" style={{ width: 70 }} /></td>
                    <td className="hide-mobile"><span className="skel" style={{ width: 100 }} /></td>
                    <td className="hide-mobile"><span className="skel" style={{ width: 80 }} /></td>
                    <td className="hide-mobile"><span className="skel" style={{ width: 30 }} /></td>
                    <td className="hide-mobile"><span className="skel" style={{ width: 100 }} /></td>
                  </tr>
                )) : items.map(item => (
                  <tr key={item.id} className="clickable" onClick={() => onOpen(item.id)}>
                    <td className="primary">{item.name}</td>
                    <td><Badge kind={statusKind(item.outputStatus)}>{item.outputStatus}</Badge></td>
                    <td className="hide-mobile">{item.jurisdiction || '—'}</td>
                    <td className="hide-mobile"><Badge kind={item.scope === 'Global' ? 'info' : 'neutral'}>{scopeLabel(item)}</Badge></td>
                    <td className="hide-mobile mono" style={{ fontSize: 12 }}>{item.documents.length}</td>
                    <td className="mono hide-mobile" style={{ fontSize: 12 }}>{fmtDateTime(item.updatedAt)}</td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>No policy inputs yet.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {drawerOpen && (
        <PolicyInputDrawer
          policyInput={null}
          canManageGlobal={canManageGlobal}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {}}
          onCreated={newId => { setDrawerOpen(false); onOpen(newId); }}
          onDeleted={() => {}}
          onUpdateConflict={() => {}}
          addToast={addToast}
        />
      )}
      <Toasts items={toasts} onRemove={removeToast} />
    </Shell>
  );
}

function PolicyInputDetail({ id, canWrite, canManageGlobal }: { id: string; canWrite: boolean; canManageGlobal: boolean }) {
  const { toasts, addToast, removeToast } = useToasts();

  const [item, setItem] = useState<PolicyInput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState<DetailTab>('details');
  const [outputDraft, setOutputDraft] = useState('');
  const [savingOutput, setSavingOutput] = useState(false);
  const [outputSaveErr, setOutputSaveErr] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaveErr, setNotesSaveErr] = useState<string | null>(null);

  // A shared-library record is read-only unless the user holds the supplier permission.
  const canEdit = canWrite && (item?.scope !== 'Global' || canManageGlobal);
  const reload = () => setReloadTick(t => t + 1);
  const reloadSettled = useSettledReload(reload);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getPolicyInput(id)
      .then(d => {
        if (cancelled) return;
        if (!d) throw new Error('Policy input not found');
        setItem(d);
        setOutputDraft(d.output ?? '');
        setNotesDraft(d.supplementaryNotes ?? '');
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, reloadTick]);

  // While a generation runs on the server there is no push for its completion, so poll lightly.
  useEffect(() => {
    if (item?.outputStatus !== 'Generating') return;
    const timer = setInterval(reload, 15_000);
    return () => clearInterval(timer);
  }, [item?.outputStatus]);

  // The malware scan finishes a few seconds after an upload, with no push to the page.
  const scanning = (item?.documents ?? []).some(d => d.scanStatus === 'Pending');
  useEffect(() => {
    if (!scanning) return;
    const timer = setInterval(reload, 4_000);
    return () => clearInterval(timer);
  }, [scanning]);

  async function uploadFile(file: File) {
    setUploading(true); setUploadErr(null);
    try {
      if (file.type !== 'application/pdf') throw new Error('Only PDF documents are supported');
      if (file.size > 20 * 1024 * 1024) throw new Error('That file is over the 20 MB limit');
      const { url, fields, s3Key } = await getPolicyInputUploadUrl(id, file.name, file.type);
      await uploadPolicyInputDocumentToS3(url, fields, file);
      await runEvent(connId => emitConfirmPolicyInputDocument(id, s3Key, file.name, getUserEmail() ?? undefined, connId));
      addToast('success', `"${file.name}" uploaded`);
      reloadSettled();
    } catch (e) {
      setUploadErr(errMessage(e, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  }

  async function viewDoc(s3Key: string) {
    setViewingDoc(s3Key);
    try {
      window.open(await getPolicyInputDownloadUrl(id, s3Key), '_blank', 'noopener');
    } catch (e) {
      addToast('error', errMessage(e, 'Failed to open document'));
    } finally {
      setViewingDoc(null);
    }
  }

  async function deleteDoc(s3Key: string, filename: string) {
    if (!window.confirm(`Remove "${filename}" from this policy input?`)) return;
    setDeletingDoc(s3Key);
    try {
      await runEvent(connId => emitDeletePolicyInputDocument(id, s3Key, getUserEmail() ?? undefined, connId));
      addToast('success', 'Document removed');
      reloadSettled();
    } catch (e) {
      addToast('error', errMessage(e, 'Failed to remove document'));
    } finally {
      setDeletingDoc(null);
    }
  }

  async function generate() {
    setGenerating(true);
    try {
      await generatePolicyInputOutput(id, getUserEmail() ?? undefined);
      addToast('info', 'Generation started — this can take a few minutes for large document sets.');
      reloadSettled();
    } catch (e) {
      addToast('error', errMessage(e, 'Failed to start generation'));
    } finally {
      setGenerating(false);
    }
  }

  async function saveOutput() {
    setOutputSaveErr(null);
    try {
      renderMarkdown(outputDraft);
    } catch (e) {
      setOutputSaveErr(`Not valid markdown: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    setSavingOutput(true);
    try {
      // Always staged through S3: the Event Service rejects emitEvent payloads over 64KB and an
      // edited output can exceed that alone. expectedValues uses version, not the old output text,
      // for the same reason.
      const { url, fields, s3Key } = await getPolicyInputOutputUploadUrl(id);
      await uploadPolicyInputOutputToS3(url, fields, outputDraft);
      await runEvent(connId => emitUpdatePolicyInput(
        id, { outputS3Key: s3Key, performedBy: getUserEmail() ?? undefined }, connId,
        item?.version ? { version: item.version } : undefined,
      ));
      addToast('success', 'Output saved');
      reloadSettled();
    } catch (e) {
      if (e instanceof Error && isStaleWriteConflict(e.message)) addToast('error', STALE_MESSAGE, true);
      else setOutputSaveErr(errMessage(e, 'Failed to save output'));
    } finally {
      setSavingOutput(false);
    }
  }

  async function saveNotes() {
    setNotesSaveErr(null);
    setSavingNotes(true);
    try {
      await runEvent(connId => emitUpdatePolicyInput(
        id, { supplementaryNotes: notesDraft, performedBy: getUserEmail() ?? undefined }, connId,
        item?.version ? { version: item.version } : undefined,
      ));
      addToast('success', 'Supplementary notes saved');
      reloadSettled();
    } catch (e) {
      if (e instanceof Error && isStaleWriteConflict(e.message)) addToast('error', STALE_MESSAGE, true);
      else setNotesSaveErr(errMessage(e, 'Failed to save supplementary notes'));
    } finally {
      setSavingNotes(false);
    }
  }

  const val = (v: string | undefined) => loading ? <span className="skel" style={{ width: 120 }} /> : (v || '—');
  const docCount = item?.documents.length ?? 0;
  const tabs: Array<{ id: DetailTab; label: string; count?: number }> = [
    { id: 'details', label: 'Details' },
    { id: 'documents', label: 'Documents', count: docCount > 0 ? docCount : undefined },
    { id: 'output', label: 'Output' },
  ];

  return (
    <Shell
      current="policy-inputs"
      crumbs={[{ label: 'Planning' }, { label: 'Policy Inputs', href: '#/policy-inputs' }, { label: item?.name ?? 'Policy input' }]}
    >
      <PageHeader
        title={loading && !item ? '…' : (item?.name ?? 'Policy input')}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {item && <Badge kind={statusKind(item.outputStatus)}>{item.outputStatus}</Badge>}
            {item && canEdit && (
              <button className="btn" onClick={() => setDrawerOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PencilIcon />Edit
              </button>
            )}
          </div>
        }
      />

      {error && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--neg)', fontSize: 12.5 }}>
          Failed to load policy input: {error}
        </div>
      )}

      {!error && (
        <>
          <div className="tabs">
            {tabs.map(({ id: tabId, label, count }) => (
              <button key={tabId} className={`tab-btn${tab === tabId ? ' active' : ''}`} onClick={() => setTab(tabId)}>
                {label}
                {count != null && <span className="tab-count">{count}</span>}
              </button>
            ))}
          </div>

          {tab === 'details' && (
            <>
              <div className="card">
                <div className="card-hd">Details</div>
                <div className="card-body">
                  <dl className="dl">
                    <div className="dl-row"><div className="dl-label">Jurisdiction</div><div className="dl-val">{val(item?.jurisdiction)}</div></div>
                    <div className="dl-row"><div className="dl-label">Visibility</div><div className="dl-val">{val(item ? scopeLabel(item) : undefined)}</div></div>
                    <div className="dl-row"><div className="dl-label">Created</div><div className="dl-val">{val(item ? fmtDateTime(item.createdAt) : undefined)}</div></div>
                    <div className="dl-row"><div className="dl-label">Updated</div><div className="dl-val">{val(item ? fmtDateTime(item.updatedAt) : undefined)}</div></div>
                  </dl>
                </div>
              </div>

              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-hd">
                  Supplementary notes
                  {canEdit && (
                    <div className="card-hd-actions">
                      <button
                        className="btn btn-primary" style={{ fontSize: 12, padding: '3px 10px' }}
                        onClick={() => void saveNotes()}
                        disabled={savingNotes || loading || notesDraft === (item?.supplementaryNotes ?? '')}
                      >
                        {savingNotes ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                    Extra evidence kept alongside this policy summary — for example appeal decisions finding settlement boundaries out of date, or the local planning authority's housing land supply position. Appended word for word after the output in every assessment that uses this policy input, and kept when the output is regenerated.
                  </div>
                  {loading ? (
                    <span className="skel" style={{ width: '100%', height: 120 }} />
                  ) : (
                    <textarea
                      value={notesDraft} onChange={e => setNotesDraft(e.target.value)}
                      disabled={!canEdit}
                      placeholder="No supplementary notes."
                      rows={8}
                      aria-label="Supplementary notes"
                    />
                  )}
                  {notesSaveErr && <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>{notesSaveErr}</div>}
                </div>
              </div>

              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-hd">Output</div>
                <div className="card-body">
                  {loading ? <span className="skel" style={{ width: 200 }} /> : item?.output ? (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: renderMarkdown(item.output) }} />
                      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line-2)' }}>{attributionText(item)}</div>
                    </>
                  ) : <span style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>—</span>}
                </div>
              </div>
            </>
          )}

          {tab === 'documents' && (
            <div className="card">
              <div className="card-hd">
                Documents
                {docCount > 0 && <span className="card-hd-sub">{docCount}</span>}
                {canEdit && (
                  <div className="card-hd-actions">
                    <label className="btn btn-primary" style={{ fontSize: 12, padding: '3px 10px', cursor: uploading ? 'default' : 'pointer' }}>
                      {uploading ? 'Uploading…' : 'Upload PDF'}
                      <input
                        type="file" accept="application/pdf" disabled={uploading}
                        style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) void uploadFile(f); e.target.value = ''; }}
                      />
                    </label>
                  </div>
                )}
              </div>
              <div className="card-body flush">
                {uploadErr && <div style={{ padding: '12px 16px 0', color: 'var(--neg)', fontSize: 12.5 }}>{uploadErr}</div>}
                <table className="tbl tbl-list">
                  <thead>
                    <tr><th>Filename</th><th className="hide-mobile">Size</th><th></th></tr>
                  </thead>
                  <tbody>
                    {loading && !item ? (
                      <tr><td><span className="skel" style={{ width: 180 }} /></td><td className="hide-mobile"><span className="skel" /></td><td></td></tr>
                    ) : (item?.documents ?? []).map(doc => {
                      const rejected = doc.scanStatus === 'Infected' || doc.scanStatus === 'Failed';
                      return (
                      <tr key={doc.s3Key}>
                        <td className="primary">
                          {doc.filename}
                          {doc.scanStatus === 'Infected' && <> <Badge kind="neg">Malware removed</Badge></>}
                          {doc.scanStatus === 'Failed' && <> <Badge kind="neg">Rejected</Badge></>}
                          {doc.scanStatus === 'Pending' && <> <Badge kind="info">Scanning…</Badge></>}
                          {doc.scanDetail && doc.scanStatus !== 'Pending' && (
                            <div style={{ color: 'var(--neg)', fontSize: 12, fontWeight: 400 }}>{doc.scanDetail}</div>
                          )}
                        </td>
                        <td className="mono hide-mobile" style={{ fontSize: 12 }}>{(doc.size / 1024).toFixed(1)} KB</td>
                        <td className="right">
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-icon btn-sm" title="View document"
                              onClick={() => void viewDoc(doc.s3Key)}
                              disabled={viewingDoc === doc.s3Key || (!!doc.scanStatus && doc.scanStatus !== 'Clean')}
                            >
                              {I.doc}
                            </button>
                            {canEdit && (
                              <button
                                className="tb-action" style={{ height: 24 }}
                                onClick={() => void deleteDoc(doc.s3Key, doc.filename)} disabled={deletingDoc === doc.s3Key}
                              >
                                {rejected ? (deletingDoc === doc.s3Key ? 'Removing…' : 'Remove') : (deletingDoc === doc.s3Key ? 'Deleting…' : 'Delete')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                    {docCount === 0 && !loading && (
                      <tr><td colSpan={3} style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>No documents uploaded yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'output' && (
            <div className="card">
              <div className="card-hd">
                Output
                {canEdit && (
                  <div className="card-hd-actions">
                    <button
                      className="btn" style={{ fontSize: 12, padding: '3px 10px' }}
                      onClick={() => void generate()}
                      disabled={generating || loading || item?.outputStatus === 'Generating' || docCount === 0}
                      title={docCount === 0 ? 'Upload at least one document first' : item?.outputStatus === 'Generating' ? 'A generation is already in progress' : undefined}
                    >
                      {generating || item?.outputStatus === 'Generating' ? 'Generating…' : 'Generate output'}
                    </button>
                    <button
                      className="btn btn-primary" style={{ fontSize: 12, padding: '3px 10px' }}
                      onClick={() => void saveOutput()}
                      disabled={savingOutput || loading || outputDraft === (item?.output ?? '')}
                    >
                      {savingOutput ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {item?.outputStatus === 'Stale' && (
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                    The uploaded documents changed since this output was last generated — regenerate to refresh it.
                  </div>
                )}
                {item?.outputStatus === 'Generating' && (
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                    Generation in progress — this can take a few minutes for large document sets. Any content below is from before this run; this page refreshes itself.
                  </div>
                )}
                {item?.outputStatus === 'Failed' && (
                  <div style={{ fontSize: 12.5, color: 'var(--neg)' }}>
                    The last generation attempt failed. Try again, or check Support Tools for the underlying error if it keeps happening.
                  </div>
                )}
                {loading && !item ? (
                  <span className="skel" style={{ width: '100%', height: 200 }} />
                ) : (
                  <div className="field">
                    <label>Markdown source</label>
                    <textarea
                      value={outputDraft} onChange={e => setOutputDraft(e.target.value)}
                      disabled={!canEdit}
                      placeholder="Write or paste markdown, or use Generate output to have AI produce it from the uploaded documents."
                      rows={28}
                      style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
                    />
                  </div>
                )}
                {outputSaveErr && <div style={{ color: 'var(--neg)', fontSize: 12.5 }}>{outputSaveErr}</div>}
                {!outputDraft && !loading && <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>No output generated yet.</div>}
                {item?.output && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', borderTop: '1px solid var(--line-2)', paddingTop: 10 }}>
                    {attributionText(item)} — last generated {item.outputGeneratedAt ? fmtDateTime(item.outputGeneratedAt) : '—'} —{' '}
                    {fmtTokens(item.lastGenerationInputTokens)} tokens in / {fmtTokens(item.lastGenerationOutputTokens)} out — est. {fmtCost(item.lastGenerationCostUsd)}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {drawerOpen && item && (
        <PolicyInputDrawer
          policyInput={item}
          canManageGlobal={canManageGlobal}
          onClose={() => setDrawerOpen(false)}
          onSaved={updated => { setItem(updated); setDrawerOpen(false); }}
          onCreated={() => {}}
          onDeleted={() => { window.location.hash = '#/policy-inputs'; }}
          onUpdateConflict={() => { setDrawerOpen(false); addToast('error', STALE_MESSAGE, true); reload(); }}
          addToast={addToast}
        />
      )}
      <Toasts items={toasts} onRemove={removeToast} />
    </Shell>
  );
}
